<template>
  <div class="table-pagination-controls d-flex align-center">
    {{ $t('rows_per_page' )}}
    <div class="page-options-wrapper mx-5">
      <v-select v-model="localRowsPerPage" :items="perPageOptions" outlined dense hide-details class="mb-1"/>
    </div>
    <span class="items-displayed">
      <span v-if="this.totalRows > 0">
        {{ $t('table_pagination_format', { start: this.from, end: this.to, total: this.totalRows }) }}
      </span>
    </span>
    <span class="ml-lg-4">
      <v-btn fab text color="primary" @click="firstPage" :small="$vuetify.breakpoint.mdAndDown">
        <v-icon>mdi-page-first</v-icon>
      </v-btn>
      <v-btn fab text color="primary" @click="decrementPage" :small="$vuetify.breakpoint.mdAndDown">
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <v-btn fab text color="primary" @click="incrementPage" :small="$vuetify.breakpoint.mdAndDown">
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
      <v-btn fab text color="primary" @click="lastPage" :small="$vuetify.breakpoint.mdAndDown">
        <v-icon>mdi-page-last</v-icon>
      </v-btn>
    </span>
  </div>
</template>

<script>

import { ceil, clamp } from 'lodash'

export default {
  name: 'TablePaginationControls',
  props: {
    /**
     * The total rows to display
     */
    totalRows: {
      type: Number
    },
    /**
     * The current page being displayed
     */
    currentPage: {
      type: Number
    },
    /**
     * The rows per page. If the value is -1, shows all items.
     */
    rowsPerPage: {
      type: Number
    },
    /**
     * The options array for the amount of items per page.
     * Takes the standard type of Vuetify options, so you can provide either numbers, or text/value pairs:
     * IE: [10, 20, 50, 100, { value: -1, text: this.$t('all') }],
     */
    perPageOptions: {
      type: Array
    }
  },
  data () {
    return {
      // Local copies to avoid changing the external properties when updating
      localCurrentPage: this.currentPage,
      localRowsPerPage: this.rowsPerPage
    }
  },
  computed: {
    showAll () {
      return this.localRowsPerPage === -1
    },
    from () {
      if (this.showAll) {
        return 1
      }
      return 1 + (this.localCurrentPage - 1) * this.localRowsPerPage
    },
    to () {
      if (this.showAll) {
        return this.totalRows
      }
      return clamp(this.localCurrentPage * this.localRowsPerPage, 1, this.totalRows)
    }
  },
  methods: {
    /**
     * Increment the page counter by 1
     */
    incrementPage () {
      if (this.showAll) {
        return
      }
      if (this.to < this.totalRows) {
        this.localCurrentPage += 1
        this.$emit('update:currentPage', this.localCurrentPage)
      }
    },
    /**
     * Decrement the page counter by 1
     */
    decrementPage () {
      if (this.showAll) {
        return
      }
      if (this.localCurrentPage > 1) {
        this.localCurrentPage -= 1
        this.$emit('update:currentPage', this.localCurrentPage)
      }
    },
    /**
     * Jump to the first page
     */
    firstPage () {
      if (this.showAll) {
        return
      }
      if (this.localCurrentPage > 1) {
        this.localCurrentPage = 1
        this.$emit('update:currentPage', this.localCurrentPage)
      }
    },
    /**
     * Jump to the last page
     */
    lastPage () {
      if (this.showAll) {
        return
      }
      const lastPage = ceil(this.totalRows / this.localRowsPerPage)
      if (this.localCurrentPage !== lastPage) {
        this.localCurrentPage = lastPage
        this.$emit('update:currentPage', this.localCurrentPage)
      }
    }
  },
  watch: {
    // Update the local copies if the external values change
    currentPage (value) {
      this.localCurrentPage = value
    },
    rowsPerPage (value) {
      this.localRowsPerPage = value
    },
    // Emit changed value when the picker changes the rows per page
    localRowsPerPage () {
      this.$emit('update:rowsPerPage', this.localRowsPerPage)
    }
  }
}
</script>

<style scoped lang="scss">
.page-options-wrapper {
  width: 100px;
}
// Give the items displayed at least a little room to expand without disturbing the page controls.
.items-displayed {
  min-width: 100px;
}
</style>
